import React from "react";
import { ArticleChangeImportance, fieldValidation, permissionGroupHelper } from "../../../amorg-common";
import { LinkButton } from "../../../designSystem/button/LinkButton";
import { Flex } from "../../../designSystem/compositeComponents/FlexHelpers";
import { ImportanceAvatar } from '../../../designSystem/avatar/ImportanceAvatar';
import { FormField } from "../../../designSystem/formField/FormField";
import { HtmlEditorViewer } from "../../../designSystem/inputs/HtmlEditorViewer";
import { toDateString } from "../../../utils/date";
import { UserSelectorButton } from "../../../designSystem/compositeComponents/itemSelector/UserSelectorAvatarButton";
import { assignArticleOwner } from "../../../api/siteArticlesClientApi";
import { LoadingPageOverlay } from "../../../designSystem/LoadingPageOverlay";
import { articleIconHelper } from "../../../utils/iconHelper";
import { Icon } from "../../../designSystem/icons/Icons";
import { H2 } from "../../../designSystem/typography/Heading";
import { NoUserAvatar } from "../../../designSystem/avatar/NoUserAvatar";
import { UserAvatar } from "../../../designSystem/avatar/UserAvatar";
import { LinkWithCopyButton } from "../../../designSystem/compositeComponents/LinkWithCopyButton";
import { Label } from "../../../designSystem/Label";
import { appUrlBuilder } from "../../../utils/appUrlBuilder";
import { SingleTagEditor } from "../../../designSystem/compositeComponents/SingleTagEditor";
import { ResponsiveGrid, ResponsiveGridItem } from "../../../designSystem/ResponsiveGrid";

type ArticleVersionFieldsProps = {
    articleId:string,
    title:string,
    description:string,
    importance: ArticleChangeImportance,
    reference:string | null,
    list:{ id:string, name:string },
    topic:{ id:string, name:string },
    date: Date | null,
    url: string,
    owner:{ userId:string, displayName:string, initials:string } | null,
    disableEdit?:boolean,
    publicLink?:{siteId:string},
    availableCategoryTags?:string[],
    selectedCategoryTag:string,
    onCategoryTagChange?:(tag:string) => void
};
export function ArticleVersionFields (props: ArticleVersionFieldsProps)
{
    const [selectedUserId, setSelectedUserId] = React.useState(props.owner?.userId || '');
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        setSelectedUserId(props.owner?.userId || '');
    }, [props.owner]);


    async function handleUserChange (userId: string) {
        setLoading(true);
        const result = await assignArticleOwner(props.articleId, userId || null);
        if (result.isSuccess)
        {
            setSelectedUserId(userId || '');
        }
        setLoading(false);
    }

    return (
        <>
            <Flex alignItems="center" gap={16}>
                <Icon name={articleIconHelper.getArticleListIcon(props.list.name)} />
                <H2 marginTop={5} marginBottom={5}>{props.title}</H2>
            </Flex>
            
            <Flex flexDirection='column' gap={16}>

                <LoadingPageOverlay loading={loading} />

                <Flex flexDirection='column' gap={16}>

                    <ResponsiveGrid rowGap={16} columnGap={16}>
                        
                        <ResponsiveGridItem desktop={4} tablet={6} mobile={12}>
                            <FormField label="Lovnummer"
                                inputComponent={
                                    props.reference
                                        ? <span>{props.reference}</span>
                                        : <span>ikke angivet</span>
                                } />
                        </ResponsiveGridItem>
                        
                        <ResponsiveGridItem desktop={4} tablet={6} mobile={12}>
                            <FormField label="Lovkravsliste"
                                inputComponent={<span>{props.list.name}</span>} />
                        </ResponsiveGridItem>
                        
                        <ResponsiveGridItem desktop={4} tablet={6} mobile={12}>
                            <FormField label="Emne"
                                inputComponent={<span>{props.topic.name}</span>} />
                        </ResponsiveGridItem>

                        <ResponsiveGridItem desktop={4} tablet={6} mobile={12}>
                            <FormField label="Lov udgivet"
                                inputComponent={
                                    props.date
                                        ? <span>{toDateString(props.date as Date)}</span>
                                        : <span>ikke angivet</span>
                                } />
                        </ResponsiveGridItem>

                        <ResponsiveGridItem desktop={4} tablet={6} mobile={12}>
                            <FormField label="Link til loven"
                                inputComponent={
                                    <span>
                                        <LinkButton text="Gå til siden" variant={"outlined"} href={props.url} target='_blank' icon="interface_actions_externalLink" />
                                    </span>
                                } />
                        </ResponsiveGridItem>

                        <ResponsiveGridItem desktop={4} tablet={6} mobile={12}>
                            <FormField label="Ansvarlig"
                                inputComponent={
                                    <span style={{ width:'fit-content' }}>
                                        {props.disableEdit
                                            ?   props.owner 
                                                    ?   <UserAvatar user={{
                                                            displayName: props.owner.displayName,
                                                            initials: props.owner.initials
                                                        }} />
                                                    :   <NoUserAvatar tooltip="Ingen valgt" />
                                            :   <UserSelectorButton
                                                    userFilter={u => permissionGroupHelper.hasMinimumPermissionGroup(u.permissionGroup.id, 'member')}
                                                    selectedUserId={selectedUserId}
                                                    onUserChange={u => handleUserChange(u?.id || '')}
                                                    disabled={props.disableEdit}
                                                />
                                        }
                                    </span>
                                } 
                            />
                        </ResponsiveGridItem>

                        <ResponsiveGridItem desktop={12}>
                            <FormField label="Estimeret betydning"
                                inputComponent={
                                    <span style={{ width:'fit-content' }}>
                                        <ImportanceAvatar importance={props.importance} size="20px" />
                                    </span>
                                } />
                        </ResponsiveGridItem>

                        <ResponsiveGridItem desktop={6} tablet={6} mobile={12}>
                            <FormField
                                label="Kategori"
                                inputComponent={
                                    (props.availableCategoryTags!==undefined && props.onCategoryTagChange!==undefined) 
                                    ?   <SingleTagEditor
                                            availableTags={props.availableCategoryTags}
                                            selectedTag={props.selectedCategoryTag}
                                            onChange={props.onCategoryTagChange}
                                            texts={{
                                                createTagOption:'Opret ny kategori',
                                                newTagPlaceholder:'Ny kategori',
                                                selectTagOption:'Vælg kategori'
                                            }}
                                            validator={fieldValidation.article.categoryTag}
                                            disabled={props.disableEdit}
                                        />
                                    :   <span>{props.selectedCategoryTag}</span>
                                }
                            />
                            
                        </ResponsiveGridItem>
                    
                        {props.publicLink &&
                            <ResponsiveGridItem desktop={12}>
                                <Flex flexDirection='column' gap={8}>
                                    <Label>Link til deling</Label>
                                    <LinkWithCopyButton url={appUrlBuilder.articles.publicLink(props.articleId, props.publicLink.siteId)} />
                                </Flex>
                            </ResponsiveGridItem>
                        }
                    </ResponsiveGrid>

                    <FormField label="Beskrivelse"
                        inputComponent={<HtmlEditorViewer content={props.description } />} />
                </Flex>
            </Flex>
        </>
    );
}

